import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { legalDocumentsApi } from '@/lib/pillarApi';
import {
    fromSystemDateTimeLocalInput,
    toSystemDateTimeLocalInput,
} from '@/lib/system-time-zone-datetime';
import { SystemTimeZoneDateTimeField } from './SystemTimeZoneDateTimeField';
import type { GetLegalDocumentResponse } from '@pillar-portal/api-types';

type LegalDocumentWithText = GetLegalDocumentResponse['legal_document'];

function fromDateTimeLocalInput(value: string, field: string): string {
    const timestamp = fromSystemDateTimeLocalInput(value);
    if (timestamp === null) {
        throw new Error(`Enter a valid ${field}.`);
    }
    return timestamp;
}

function savedContentDiffers(
    saved: LegalDocumentWithText,
    submittedText: string,
    submittedContentType: string
): boolean {
    return (
        saved.legal_document_text !== submittedText ||
        saved.legal_document_content_type !== submittedContentType.trim()
    );
}

interface LegalDocumentEditorProps {
    readonly legalDocument: LegalDocumentWithText;
    readonly onSaved: (legalDocument: LegalDocumentWithText) => void;
    readonly onCancel: () => void;
}

/**
 * Edits a legal document in place. People who already accepted it keep the
 * text they accepted on record; they are not asked to accept it again.
 */
export function LegalDocumentEditor({
    legalDocument,
    onSaved,
    onCancel,
}: LegalDocumentEditorProps) {
    const [title, setTitle] = useState(legalDocument.legal_document_title);
    const [version, setVersion] = useState(legalDocument.legal_document_version);
    const [contentType, setContentType] = useState(legalDocument.legal_document_content_type);
    const [text, setText] = useState(legalDocument.legal_document_text);
    const [inForceStart, setInForceStart] = useState(
        toSystemDateTimeLocalInput(legalDocument.in_force_start)
    );
    const [inForceEnd, setInForceEnd] = useState(
        legalDocument.in_force_end === null
            ? ''
            : toSystemDateTimeLocalInput(legalDocument.in_force_end)
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [savedWithChanges, setSavedWithChanges] = useState<LegalDocumentWithText | null>(null);
    const mutation = useMutation({
        mutationFn: async () =>
            await legalDocumentsApi.updateLegalDocument(legalDocument.legal_document_uuid, {
                legal_document_title: title.trim(),
                legal_document_version: version.trim(),
                legal_document_text: text,
                legal_document_content_type: contentType.trim(),
                in_force_start: fromDateTimeLocalInput(inForceStart, 'start date'),
                in_force_end:
                    inForceEnd.length === 0 ? null : fromDateTimeLocalInput(inForceEnd, 'end date'),
            }),
        onSuccess: (response) => {
            setErrorMessage(null);
            const saved = response.legal_document;
            if (!savedContentDiffers(saved, text, contentType)) {
                onSaved(saved);
                return;
            }
            // The server sanitized or converted the content: show what was
            // stored before leaving the editor.
            setText(saved.legal_document_text);
            setContentType(saved.legal_document_content_type);
            setSavedWithChanges(saved);
        },
        onError: (error: unknown) => {
            setErrorMessage(
                error instanceof Error ? error.message : 'Unable to save the legal document.'
            );
        },
    });

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Edit document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                    People who already accepted this document keep the text they accepted on record
                    and are not asked to accept it again. To require acceptance of new text, create
                    a new version with the same title and assign it.
                </p>
                {savedWithChanges === null ? null : (
                    <div
                        role="status"
                        className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                    >
                        Saved. PILLAR changed the content when saving it: unsafe HTML is removed
                        and Markdown is converted to HTML. The text below is what was stored and
                        what people will review.
                    </div>
                )}
                {errorMessage === null ? null : (
                    <div
                        role="alert"
                        className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                    >
                        {errorMessage}
                    </div>
                )}
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="legal-document-edit-title">Title</Label>
                        <Input
                            id="legal-document-edit-title"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="legal-document-edit-version">Version</Label>
                        <Input
                            id="legal-document-edit-version"
                            value={version}
                            onChange={(event) => setVersion(event.target.value)}
                        />
                    </div>
                    <SystemTimeZoneDateTimeField
                        id="legal-document-edit-start"
                        label="In force from"
                        value={inForceStart}
                        onChange={setInForceStart}
                    />
                    <SystemTimeZoneDateTimeField
                        id="legal-document-edit-end"
                        label="In force until"
                        value={inForceEnd}
                        onChange={setInForceEnd}
                    />
                    <div className="space-y-2">
                        <Label htmlFor="legal-document-edit-content-type">Content type</Label>
                        <Input
                            id="legal-document-edit-content-type"
                            aria-describedby="legal-document-edit-content-type-hint"
                            value={contentType}
                            onChange={(event) => setContentType(event.target.value)}
                        />
                        <p
                            id="legal-document-edit-content-type-hint"
                            className="text-xs text-gray-500"
                        >
                            text/html, text/markdown or text/plain. Images and styles must be
                            embedded; external resources are rejected.
                        </p>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="legal-document-edit-text">Text</Label>
                    <textarea
                        id="legal-document-edit-text"
                        className="h-80 w-full rounded-md border px-3 py-2 font-mono text-sm"
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    {savedWithChanges === null ? null : (
                        <Button
                            disabled={mutation.isPending}
                            onClick={() => onSaved(savedWithChanges)}
                        >
                            Done
                        </Button>
                    )}
                    <Button
                        disabled={
                            mutation.isPending || title.trim().length === 0 || text.length === 0
                        }
                        onClick={() => {
                            setErrorMessage(null);
                            mutation.mutate();
                        }}
                    >
                        {mutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    {savedWithChanges === null ? (
                        <Button variant="outline" disabled={mutation.isPending} onClick={onCancel}>
                            Cancel
                        </Button>
                    ) : null}
                </div>
            </CardContent>
        </Card>
    );
}
